import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiArrowRight, FiShield, FiHeart, FiTruck } from "react-icons/fi";
import MailChecker from "mailchecker";

import MainContainer from "../../../components/utils/MainContainer";
import Seo from "../../../components/utils/Seo";
import ProductCard from "../../../components/portal/ProductCard";
import NewsCard from "../../../components/portal/NewsCard";
import NewsletterPreferencesModal from "../../../components/utils/NewsletterPreferencesModal";
import { toast } from "../../../components/modern-ui/sonner";

import bandsService from "../../../api/services/bands";
import useReveal from "../../../hooks/useReveal";

import { selectFeaturedProducts } from "../../../store/slices/products/slice";
import { selectFeaturedNews } from "../../../store/slices/news/slice";
import { getFeaturedProducts } from "../../../store/slices/products/asyncThunks";
import { getFeaturedNews } from "../../../store/slices/news/asyncThunks";
import { createNewsletter } from "../../../store/slices/newsletters/asyncThunks";
import { selectNewsletterLoading } from "../../../store/slices/newsletters/slice";

import {
  Wrap,
  Container,
  Hero,
  HeroInner,
  HeroContent,
  HeroLabel,
  HeroH1,
  HeroSub,
  HeroCtas,
  PrimaryButton,
  GhostButton,
  Section,
  SectionHead,
  SectionLabel,
  SectionH2,
  SectionLink,
  BandMarquee,
  BandMarqueeTrack,
  BandRailCard,
  BandRailInfo,
  ProductsGrid,
  NewsGrid,
  ValueStrip,
  ValueStripInner,
  ValueItem,
  NewsletterCard,
  NewsletterTitle,
  NewsletterSub,
  NewsletterForm,
  NewsletterInput,
} from "./HomePageNew.styles";

const HomePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: featuredProducts } = useSelector(selectFeaturedProducts);
  const { data: featuredNews } = useSelector(selectFeaturedNews);
  const newsletterLoading = useSelector(selectNewsletterLoading);

  const [bands, setBands] = useState([]);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Scroll-reveal po sekcijama
  const [bandsRef, bandsVisible] = useReveal();
  const [productsRef, productsVisible] = useReveal();
  const [valueRef, valueVisible] = useReveal();
  const [newsRef, newsVisible] = useReveal();
  const [newsletterRef, newsletterVisible] = useReveal();

  useEffect(() => {
    dispatch(getFeaturedProducts())
      .unwrap()
      .catch((err) => console.error(err.message));
    dispatch(getFeaturedNews())
      .unwrap()
      .catch((err) => console.error(err.message));
  }, [dispatch]);

  // Bend traka, prvih 12 bendova sa cover slikama
  useEffect(() => {
    let active = true;
    bandsService
      .getBands({ page: 1, limit: 12 })
      .then((response) => {
        if (active) setBands(response?.data || []);
      })
      .catch(() => {
        if (active) setBands([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const goTo = (path) => {
    navigate(path);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  /* ----------------------------- newsletter ----------------------------- */
  const handleNewsletterClick = (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newsletterEmail)) {
      toast.error("Molimo Vas unesite važeću email adresu");
      return;
    }
    if (!MailChecker.isValid(newsletterEmail)) {
      toast.error("Molimo Vas unesite važeću email adresu");
      return;
    }

    setShowPreferencesModal(true);
    setModalError(null);
  };

  const handleSubscribeWithPreferences = ({ email, preferences }) => {
    dispatch(createNewsletter({ email, preferences }))
      .unwrap()
      .then(() => {
        setNewsletterEmail("");
        setShowPreferencesModal(false);
        toast.success("Uspešno ste se pretplatili na newsletter!");
      })
      .catch((err) => {
        setModalError(err.message || "Greška prilikom pretplate");
      });
  };

  const closeModal = () => {
    setShowPreferencesModal(false);
    setModalError(null);
  };

  const products = (featuredProducts || []).slice(0, 8);
  const news = (featuredNews || []).slice(0, 3);

  // Marquee: sadržaj se duplira da translateX(-50%) petlja bude bešavna.
  // Broj kopija mora biti paran, a polovina trake šira od viewporta,
  // zato se malo bendova duplira više puta.
  const marqueeCopies =
    bands.length > 0 ? Math.max(2, 2 * Math.ceil(10 / bands.length)) : 2;
  const marqueeBands = Array.from({ length: marqueeCopies }, () => bands).flat();
  // ~1.9s po kartici → ista brzina bez obzira na broj bendova
  const marqueeDuration = `${Math.round(marqueeBands.length * 1.9)}s`;

  return (
    <MainContainer>
      {/* SEO: podrazumevani naslov/opis + Organization i WebSite schema */}
      <Seo
        canonical="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "МРЧ",
            url: "https://mrc.rs",
            logo: "https://mrc.rs/logo.png",
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "МРЧ",
            url: "https://mrc.rs",
          },
        ]}
      />
      <Wrap>
        {/* SVG filter za sprej efekat na hero naslovu, grube, rasprskane
            ivice narandžastog bloka (feTurbulence + feDisplacementMap) */}
        <svg
          width="0"
          height="0"
          style={{ position: "absolute" }}
          aria-hidden="true"
          focusable="false"
        >
          <filter
            id="spray-edges"
            x="-25%"
            y="-45%"
            width="150%"
            height="190%"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.11 0.16"
              numOctaves="3"
              seed="7"
              result="noise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale="16"
              xChannelSelector="R"
              yChannelSelector="G"
            />
            <feGaussianBlur stdDeviation="0.45" />
          </filter>
        </svg>

        {/* 2.1 Hero */}
        <Hero>
          <HeroInner>
            <HeroContent>
              <HeroLabel>Zvaničan merch domaće scene</HeroLabel>
              <HeroH1>
                Nosi muziku <em>koju slušaš</em>
              </HeroH1>
              <HeroSub>
                Zvanični proizvodi. Deo svake kupovine ide direktno bendu.
              </HeroSub>
              <HeroCtas>
                <PrimaryButton
                  type="button"
                  onClick={() => goTo("/prodavnica?page=1")}
                >
                  U prodavnicu
                </PrimaryButton>
                <GhostButton
                  type="button"
                  onClick={() => goTo("/bendovi?page=1")}
                >
                  Bendovi <FiArrowRight size={16} />
                </GhostButton>
              </HeroCtas>
            </HeroContent>
          </HeroInner>
        </Hero>

        {/* 2.2 Bend traka, marquee preko cele širine, pauza + fokus na hover */}
        {bands.length > 0 && (
          <Section ref={bandsRef}>
            <Container>
              <SectionHead $visible={bandsVisible}>
                <div>
                  <SectionLabel>01 / Bendovi</SectionLabel>
                  <SectionH2>Scena je ovde</SectionH2>
                </div>
                <SectionLink onClick={() => goTo("/bendovi?page=1")}>
                  Svi bendovi <FiArrowRight size={15} />
                </SectionLink>
              </SectionHead>
            </Container>

            <BandMarquee
              $visible={bandsVisible}
              style={{ "--marquee-duration": marqueeDuration }}
            >
              <BandMarqueeTrack>
                {marqueeBands.map((band, i) => {
                  // Druga polovina trake su klonovi, skriveni za čitače
                  // ekrana i u fallback (scroll) varijanti
                  const isClone = i >= bands.length;
                  return (
                    <BandRailCard
                      key={`${band.id}-${i}`}
                      data-clone={isClone ? "true" : undefined}
                      aria-hidden={isClone ? "true" : undefined}
                      onClick={() => goTo(`/bend/${band.slug || band.id}`)}
                    >
                      {(band.bannerImage || band.mainImage) && (
                        <img
                          src={band.bannerImage || band.mainImage}
                          alt={band.name}
                          loading="lazy"
                        />
                      )}
                      <BandRailInfo>
                        <strong>{band.name}</strong>
                        <span>
                          {band.numOfProducts > 0
                            ? `${band.numOfProducts} ${band.numOfProducts === 1 ? "proizvod" : "proizvoda"}`
                            : "Merch uskoro"}
                        </span>
                      </BandRailInfo>
                    </BandRailCard>
                  );
                })}
              </BandMarqueeTrack>
            </BandMarquee>
          </Section>
        )}

        {/* 2.3 Istaknuti proizvodi */}
        {products.length > 0 && (
          <Container>
            <Section ref={productsRef}>
              <SectionHead $visible={productsVisible}>
                <div>
                  <SectionLabel>02 / Istaknuto</SectionLabel>
                  <SectionH2>Istaknuti proizvodi</SectionH2>
                </div>
                <SectionLink onClick={() => goTo("/prodavnica?page=1")}>
                  Cela prodavnica <FiArrowRight size={15} />
                </SectionLink>
              </SectionHead>

              <ProductsGrid $visible={productsVisible}>
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </ProductsGrid>
            </Section>
          </Container>
        )}

        {/* 2.4 Value strip */}
        <ValueStrip ref={valueRef}>
          <ValueStripInner $visible={valueVisible}>
            <ValueItem>
              <FiShield size={17} />
              <span>Zvanično licencirano</span>
            </ValueItem>
            <ValueItem>
              <FiHeart size={17} />
              <span>Bend dobija deo od svake kupovine</span>
            </ValueItem>
            <ValueItem>
              <FiTruck size={17} />
              <span>Slanje PostExpresom u Srbiju, Crnu Goru i BiH</span>
            </ValueItem>
          </ValueStripInner>
        </ValueStrip>

        {/* 2.5 Novosti */}
        {news.length > 0 && (
          <Container>
            <Section ref={newsRef}>
              <SectionHead $visible={newsVisible}>
                <div>
                  <SectionLabel>03 / Novosti</SectionLabel>
                  <SectionH2>Sa scene</SectionH2>
                </div>
                <SectionLink onClick={() => goTo("/novosti?page=1")}>
                  Sve novosti <FiArrowRight size={15} />
                </SectionLink>
              </SectionHead>

              <NewsGrid $visible={newsVisible}>
                {news.map((newsItem) => (
                  <NewsCard key={newsItem.id} news={newsItem} />
                ))}
              </NewsGrid>
            </Section>
          </Container>
        )}

        {/* 2.6 Newsletter */}
        <Container>
          <Section>
            <NewsletterCard ref={newsletterRef} $visible={newsletterVisible}>
              <div>
                <NewsletterTitle>Budi u toku</NewsletterTitle>
                <NewsletterSub>
                  Novi proizvodi, bendovi i akcije. Bez spama, odjava jednim
                  klikom.
                </NewsletterSub>
              </div>
              <NewsletterForm onSubmit={handleNewsletterClick}>
                <NewsletterInput
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="tvoj@email.com"
                  aria-label="Email adresa"
                />
                <PrimaryButton type="submit" disabled={newsletterLoading}>
                  {newsletterLoading ? "Šaljem..." : "Prijavi se"}
                </PrimaryButton>
              </NewsletterForm>
            </NewsletterCard>
          </Section>
        </Container>
      </Wrap>

      {/* Newsletter preferences modal */}
      <NewsletterPreferencesModal
        isOpen={showPreferencesModal}
        onClose={closeModal}
        email={newsletterEmail}
        onSubscribe={handleSubscribeWithPreferences}
        loading={newsletterLoading}
        error={modalError}
      />
    </MainContainer>
  );
};

export default HomePage;
